import { useCallback, useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  DataGridPremium, GridRowClassNameParams, GRID_DETAIL_PANEL_TOGGLE_FIELD,
  type GridPinnedColumnFields, type GridColumnResizeParams, type GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { trackingService } from '../../services/tracking.service';
import { dayBounds } from '../../utils/timezone';
import { trafficSourcesService } from '../../services/traffic-sources.service';
import { flowsService } from '../../services/flows.service';
import { hiddenFrom, trafficSourceVarNames } from '../../utils/tracking-helpers';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useModalState } from '../../hooks/useModalState';
import { isEntityField } from '../../components/entity-fetchers';
import { ENTITY_MODAL_NAMES, GROUP_OPTION_VALUES, formatDimValue, labelForField, gridSx, isSundayKey, levelDims, dimField, sortForLevel } from './tracking-page.format';
import { ConversionTypeColumn, columnCatalogue, exportColumns, reportColumns } from './tracking-page.columns';
import { useColumnPrefs } from '../../hooks/useColumnPrefs';
import { useAuthStore } from '../../stores/auth.store';
import { useNotesEditing } from '../../hooks/useNotesEditing';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useReportState } from './useReportState';
import { useRowActions } from './useRowActions';
import TrackingToolbar from './TrackingToolbar';
import WorkspaceOverviewStrip from './WorkspaceOverviewStrip';
import DrilldownPanel from './DrilldownPanel';
import ColumnsDialog from './ColumnsDialog';
import LeadBreakdownDialog from './LeadBreakdownDialog';
import RowContextMenu from './RowContextMenu';
import { useColumnWidths } from '../../hooks/useColumnWidths';

// the grid drops absent fields
const DESKTOP_PINNED = { left: [GRID_DETAIL_PANEL_TOGGLE_FIELD, '_profitability', 'display_name', ...GROUP_OPTION_VALUES.map(dimField)] };

export default function TrackingPage() {
  const report = useReportState();
  const {
    groupBy, drilldownGroupBy, drilldownChain, timezone, excludeBots, convByClick, compare, tagIds,
    sortModel, setSortModel, filters, setFilters,
    pagination, setPagination,
    dateFrom, dateTo, filterParams, filtersKey, tagsParam, sortBy, sortOrder,
  } = report;

  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);

  // Tag lookup for the grouped entity: the stats rows carry only ids, so tags are
  // resolved client-side from the entity list.
  const { data: flowsForTags } = useQuery({
    queryKey: ['tracking-flow-tags'],
    queryFn: () => flowsService.query({ limit: 1000 }),
  });
  const flowTagMap = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const f of (flowsForTags?.data || [])) m.set(f.id, f.tag_ids || []);
    return m;
  }, [flowsForTags]);

  // A single active traffic-source filter lets the v1-v10 columns show that source's
  // real variable names instead of the slot numbers.
  const tsFilterValues = filters.filter(f => f.field === 'traffic_source_id').map(f => f.value);
  const activeTsId = tsFilterValues.length === 1 ? tsFilterValues[0] : '';
  const { data: activeTs } = useQuery({
    queryKey: ['ts-vnames', activeTsId],
    queryFn: () => trafficSourcesService.getById(activeTsId),
    enabled: !!activeTsId,
  });
  const vNames = useMemo(() => trafficSourceVarNames(activeTs || null), [activeTs]);
  const vLabel = useCallback(
    // Prefixed with the slot, Voluum style, e.g. "V2: Campaign ID".
    (field: string): string => {
      if (field.includes('+')) return levelDims(field).map((d) => vLabel(d)).join(' × ');
      const real = vNames[field];
      if (real) return `${field.toUpperCase()}: ${real}`;
      return labelForField(field);
    },
    [vNames],
  );

  // A second round trip, so only ask when a chip column is on screen. From the stored
  // preference, not the built columns: those depend on the conversion types this returns.
  const hiddenCols = useAuthStore((st) => st.user?.settings?.tracking?.hidden_columns);
  const wantsChips = useMemo(() => {
    if (levelDims(groupBy).length > 1) return false;
    const shown = (field: string, groupedBy: string) => groupBy !== groupedBy && !hiddenCols?.includes(field);
    return shown('_flows', 'flow_id') || shown('_offers', 'offer_id')
      || shown('_landing_pages', 'landing_page_id');
  }, [groupBy, hiddenCols]);

  const [settled, pendingSettle] = useDebouncedValue({
    dateFrom, dateTo, groupBy, timezone, excludeBots, convByClick, compare, sortBy, sortOrder,
    filterParams, tagsParam,
    page: pagination.page, pageSize: pagination.pageSize, wantsChips,
  }, 350);

  const { data: stats, isFetching, refetch } = useQuery({
    placeholderData: (prev) => prev,
    queryKey: ['tracking-stats', settled],
    queryFn: () => trackingService.getStats({
      ...dayBounds(settled.dateFrom, settled.dateTo),
      group_by: levelDims(settled.groupBy),
      ...settled.filterParams,
      ...settled.tagsParam,
      sort_by: settled.sortBy,
      sort_order: settled.sortOrder,
      timezone: settled.timezone,
      include_bots: !settled.excludeBots,
      compare: settled.compare,
      conv_time_basis: settled.convByClick ? 'click' : 'conversion',
      include_breakdowns: settled.wantsChips,
      limit: settled.pageSize,
      offset: settled.page * settled.pageSize,
    }),
  });

  // Inline entity editing: open the matching entity editor (global AutoModals) from a
  // grouped row, and refresh the report when it closes so a rename is reflected.
  const navigate = useNavigate();
  const offerModal = useModalState('offer');
  const flowModal = useModalState('flow');
  const landingPageModal = useModalState('landing-page');
  const openEntityEditor = useCallback((field: string, id: string, reason: 'Update' | 'Duplicate' = 'Update') => {
    if (field === 'network_id') { navigate(`/networks/${id}${reason === 'Duplicate' ? '/duplicate' : ''}`); return; }
    if (field === 'traffic_source_id') { navigate(`/traffic-sources/${id}${reason === 'Duplicate' ? '/duplicate' : ''}`); return; }
    const setModal = {
      offer_id: offerModal.setModal, flow_id: flowModal.setModal,
      landing_page_id: landingPageModal.setModal,
    }[field];
    setModal?.({ open: true, id, reason });
  }, [navigate, offerModal.setModal, flowModal.setModal, landingPageModal.setModal]);

  const anyEntityModalOpen = offerModal.modalState.open
    || flowModal.modalState.open || landingPageModal.modalState.open;
  const prevEntityModalOpen = useRef(anyEntityModalOpen);
  useEffect(() => {
    // Editor just closed (e.g. after a save). Names in reports come from a ClickHouse
    // dictionary cached up to 5min, so force-reload the edited entity's dict, THEN
    // refetch — otherwise a rename wouldn't show until the dict expires.
    if (prevEntityModalOpen.current && !anyEntityModalOpen) {
      const entities = levelDims(groupBy).map((d) => ENTITY_MODAL_NAMES[d]).filter(Boolean);
      (async () => {
        for (const entity of entities) {
          try { await trackingService.reloadDictionary(entity); } catch { /* fall through to refetch */ }
        }
        refetch();
      })();
    }
    prevEntityModalOpen.current = anyEntityModalOpen;
  }, [anyEntityModalOpen, refetch, groupBy]);

  const rows = (stats?.rows || []).map((r, i) => ({
    id: r.group_key ?? `_${i}`, ...r,
    _breakdown: (stats as any)?.breakdowns?.[String(r.group_key ?? '')],
  }));
  const totals = stats?.totals;
  const conversionTypeColumns: ConversionTypeColumn[] = (stats as any)?.conversion_type_columns || [];
  const prevTotals = (stats as any)?.previous_totals as Record<string, unknown> | undefined;
  // The server decides: asking to compare on an unpaged report is refused.
  const comparing = !!prevTotals;

  const totalsRow = totals ? [{
    id: '__totals__',
    group_key: 'Total',
    name: `Total (${stats?.total_row_count ?? rows.length})`,
    display_name: `Total (${stats?.total_row_count ?? rows.length})`,
    visits: Number((totals as any).visits || 0),
    bot_visits: Number((totals as any).bot_visits || 0),
    unique_visits: Number((totals as any).unique_visits || 0),
    clicks: Number((totals as any).clicks || 0),
    unique_clicks: Number((totals as any).unique_clicks || 0),
    conversions: totals.conversions,
    revenue: totals.revenue,
    cost: Number((totals as any).cost || 0),
    profit: Number((totals as any).profit || 0),
    ctr: Number((totals as any).ctr || 0),
    cv_rate: Number((totals as any).cv_rate || 0),
    cr: Number((totals as any).cr || 0),
    epv: Number((totals as any).epv || 0),
    cpv: Number((totals as any).cpv || 0),
    roi: Number((totals as any).roi || 0),
    roas: Number((totals as any).roas || 0),
    rpl: Number((totals as any).rpl || 0),
    cpl: Number((totals as any).cpl || 0),
    ...Object.fromEntries(conversionTypeColumns.map((ct) => [ct.field, Number((totals as any)[ct.field] || 0)])),
    ...(prevTotals ? {
      profit_delta: Number((totals as any).profit || 0) - Number(prevTotals.profit || 0),
      ...Object.fromEntries(Object.entries(prevTotals).map(([k, v]) => [`prev_${k}`, Number(v) || 0])),
    } : {}),
  }] : undefined;

  const groupLabel = vLabel(groupBy);
  const theme = useTheme();

  // Read once at mount for the same reason as the pinning below.
  const isNarrow = window.innerWidth < theme.breakpoints.values.md;

  const notesEditing = useNotesEditing(groupBy);
  const allColumns = useMemo(
    () => reportColumns({ canEditNotes: notesEditing.editable, groupBy, groupLabel, dimLabel: vLabel, conversionTypeColumns, flowTagMap, onEditEntity: openEntityEditor, narrow: isNarrow, compare: comparing, costEstimated: (stats as any)?.cost_basis === 'estimated' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupBy, groupLabel, vLabel, stats, flowTagMap, openEntityEditor, isNarrow, comparing, notesEditing.editable],
  );

  const [columnsOpen, setColumnsOpen] = useState(false);
  // Pinned columns eat a third of a phone's width. Read once at mount, not via
  // useMediaQuery, whose first render reports false whatever the screen.
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>(() =>
    (isNarrow ? { left: [] } : DESKTOP_PINNED));
  // The grid pins in list order, so a combination's columns follow the level, not the option list.
  const orderedPins = useMemo<GridPinnedColumnFields>(() => {
    const left = pinnedColumns.left ?? [];
    const dimPins = levelDims(groupBy).map(dimField).filter((f) => left.includes(f));
    const rest = left.filter((f) => !f.startsWith('_dim_'));
    const at = rest.includes('display_name') ? rest.indexOf('display_name') + 1 : rest.length;
    return { ...pinnedColumns, left: [...rest.slice(0, at), ...dimPins, ...rest.slice(at)] };
  }, [pinnedColumns, groupBy]);
  const gridSortModel = useMemo(() => sortForLevel(sortModel, groupBy), [sortModel, groupBy]);
  const catalogue = useMemo(
    () => columnCatalogue(conversionTypeColumns, groupBy, comparing), [conversionTypeColumns, groupBy, comparing]);
  const catalogueOrder = useMemo(() => catalogue.map((c) => c.field), [catalogue]);
  const { columns: orderedColumns, visibility, save: saveColumns, order: colOrder, hidden: colHidden } =
    useColumnPrefs(allColumns, catalogueOrder);

  const { columns, remember: rememberWidth } = useColumnWidths('report', orderedColumns);

  const visibleColumns = useMemo(
    () => columns.filter((c) => visibility[c.field] !== false), [columns, visibility]);
  const csvColumns = useMemo(
    () => exportColumns(visibleColumns, comparing), [visibleColumns, comparing]);

  const getDetailPanelContent = useCallback((params: any) => {
    if (!drilldownGroupBy) return null;
    return (
      // key includes the filters so an already-open panel remounts (and refetches) when
      // the toggle/date/tz change — MUI caches detail-panel content otherwise.
      <DrilldownPanel
        key={`${dateFrom}|${dateTo}|${timezone}|${excludeBots}|${convByClick}|${compare}|${filtersKey}|${tagIds.join(',')}|${drilldownChain.join('>')}`}
        parent={params.row.keys ?? { [groupBy]: String(params.row.group_key) }}
        childGroupBy={drilldownGroupBy}
        deeperLevels={drilldownChain.slice(1)}
        dateFrom={dateFrom}
        dateTo={dateTo}
        timezone={timezone}
        excludeBots={excludeBots}
        compare={compare}
        convByClick={convByClick}
        extraFilters={{ ...filterParams, ...tagsParam }}
        conversionTypeColumns={conversionTypeColumns}
        parentSortModel={gridSortModel}
        vNames={vNames}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, drilldownGroupBy, drilldownChain, dateFrom, dateTo, timezone, excludeBots, convByClick, compare, filterParams, tagsParam, filtersKey, tagIds, stats, gridSortModel, vNames]);

  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const actions = useRowActions({ dateFrom, dateTo, timezone, filterParams, tagIds });
  const { contextMenu, setContextMenu, leadBreakdown, setLeadBreakdown } = actions;

  const handleRowContextMenu = useCallback((e: MouseEvent<HTMLElement>) => {
    const id = e.currentTarget.getAttribute('data-id');
    if (!id || id === '__totals__') return;
    const row = rows.find((r) => String(r.id) === id);
    if (!row) return;
    e.preventDefault();
    const dims = levelDims(groupBy);
    const cell = (e.target as HTMLElement).closest('[data-field]')?.getAttribute('data-field') ?? '';
    const field = dims.find((d) => dimField(d) === cell) ?? dims[0];
    const value = row.keys?.[field] ?? row.group_key;
    setContextMenu({
      mouseX: e.clientX + 2,
      mouseY: e.clientY - 6,
      field,
      value: String(value ?? ''),
      label: row.names?.[field] || formatDimValue(field, value),
    });
  }, [rows, groupBy, setContextMenu]);

  // Right-click "Report" → add the clicked value to the unified filters. Entity fields
  // accumulate (multi-select); non-entity fields (country/v1/os/…) replace.
  const applyReport = useCallback(() => {
    if (!contextMenu) return;
    const { field, value, label } = contextMenu;
    setFilters(prev => {
      const base = isEntityField(field) ? prev : prev.filter(f => f.field !== field);
      if (base.some(f => f.field === field && f.value === value)) return base;
      return [...base, { field, value, label }];
    });
    setContextMenu(null);
  }, [contextMenu, setFilters, setContextMenu]);

  return (
    <Box>
      <WorkspaceOverviewStrip />
      <TrackingToolbar
        report={report}
        containerRef={filterRef}
        vLabel={vLabel}
        rows={rows}
        columns={csvColumns}
        onRefresh={() => refetch()}
        onPickColumns={() => setColumnsOpen(true)}
      />

      <DataGridPremium
        rows={rows}
        columns={columns}
        onColumnWidthChange={(params: GridColumnResizeParams) => rememberWidth(params.colDef.field, params.width)}
        columnVisibilityModel={visibility}
        onColumnVisibilityModelChange={(m: GridColumnVisibilityModel) => saveColumns(colOrder, hiddenFrom(m))}
        loading={isFetching || pendingSettle}
        density="compact"
        rowHeight={comparing ? 54 : undefined}
        sortingMode="server"
        // No unsorted state: an empty model falls through to the server's default, not the user's.
        sortingOrder={['desc', 'asc']}
        sortModel={gridSortModel}
        onSortModelChange={setSortModel}
        pinnedRows={totalsRow ? { top: totalsRow } : undefined}
        getDetailPanelContent={drilldownGroupBy ? getDetailPanelContent : undefined}
        getDetailPanelHeight={drilldownGroupBy ? getDetailPanelHeight : undefined}
        pagination
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        rowCount={stats?.total_row_count ?? 0}
        pageSizeOptions={[50, 100, 200]}
        pinnedColumns={orderedPins}
        onPinnedColumnsChange={setPinnedColumns}
        disableRowSelectionOnClick
        {...notesEditing.gridProps}
        getRowClassName={(params: GridRowClassNameParams) => (groupBy === 'date' && isSundayKey(params.row.group_key) ? 'sunday-row' : '')}
        slotProps={{ row: { onContextMenu: handleRowContextMenu, style: { cursor: 'context-menu' } } }}
        sx={{
          ...gridSx,
          height: gridHeight,
          '& .group-name-cell': { fontWeight: 600 },
          // Inline edit button reveals on row hover.
          '& .row-edit-btn': { opacity: 0, transition: 'opacity .1s' },
          '& .MuiDataGrid-row:hover .row-edit-btn': { opacity: 0.7 },
          '& .row-edit-btn:hover': { opacity: 1 },
        }}
      />

      <LeadBreakdownDialog
        state={leadBreakdown}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onClose={() => setLeadBreakdown(null)}
      />

      <ColumnsDialog
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        catalogue={catalogue}
        order={colOrder}
        hidden={colHidden}
        onSave={saveColumns}
      />

      <RowContextMenu
        contextMenu={contextMenu}
        onClose={() => setContextMenu(null)}
        onReport={applyReport}
        onCopyUrl={actions.copyEntityUrl}
        onLeadBreakdown={actions.openLeadBreakdown}
        onViewLog={actions.openRowLog}
        onDuplicate={() => {
          if (contextMenu) openEntityEditor(contextMenu.field, contextMenu.value, 'Duplicate');
          setContextMenu(null);
        }}
      />
    </Box>
  );
}
